/**
 * Provides user-friendly error messages for booking-related errors
 * @param {Object} error - The error object from the API
 * @returns {string} - User-friendly error message
 */
export const getBookingErrorMessage = (error) => {
  // Handle Spektrix API errors with problems array
  if (error.problems && Array.isArray(error.problems)) {
    // Check for specific error patterns
    if (error.problems.some(problem => problem.includes('Event instance is not on sale'))) {
      return 'This performance is not currently available for booking. Please check the booking dates or contact us for assistance.'
    }
    
    if (error.problems.some(problem => problem.includes('Invalid TicketType'))) {
      return 'There was an issue with the ticket type selection. Please try again or contact us for assistance.'
    }
    
    if (error.problems.some(problem => problem.includes('Supply either a seat for reserved areas'))) {
      return 'There was an issue with the seating selection. Please try again or contact us for assistance.'
    }
    
    // For other problems, return a generic message
    return 'Unable to add tickets to basket. Please try again or contact us for assistance.'
  }
  
  // Handle other error formats
  if (error.message) {
    if (error.message.includes('modify ticket types')) {
      return error.message
    }
    
    if (error.message.includes('Event instance is not on sale')) {
      return 'This performance is not currently available for booking. Please check the booking dates or contact us for assistance.'
    }
    
    return error.message
  }
  
  // Fallback for unknown error formats
  return 'An error occurred while updating your basket. Please try again or contact us for assistance.'
}

/**
 * Check if error is related to priority booking/membership requirement
 * @param {Object} error - The error object from the API
 * @returns {boolean} - True if this is a membership-related error
 */
export const isPriorityBookingError = (error) => {
  if (error.problems && Array.isArray(error.problems)) {
    return error.problems.some(problem => 
      problem.includes('membership') || 
      problem.includes('priority') ||
      problem.includes('not on sale') ||
      problem.includes('Event instance is not on sale')
    )
  }
  
  if (error.message) {
    return error.message.includes('membership') || 
           error.message.includes('priority') ||
           error.message.includes('not on sale') ||
           error.message.includes('Event instance is not on sale')
  }
  
  return false
}

/**
 * Shows a user-friendly error message for booking errors
 * @param {Object} error - The error object from the API
 * @param {Object} options - Additional options for error handling
 * @param {Function} options.onPriorityBookingError - Callback for priority booking errors
 */
export const showBookingError = (error, options = {}) => {
  // Check if this is a priority booking error and we have a handler
  if (isPriorityBookingError(error) && options.onPriorityBookingError) {
    options.onPriorityBookingError()
    return
  }
  
  // Otherwise show regular alert
  const message = getBookingErrorMessage(error)
  alert(message)
}